import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LoadingState } from '@tsin-core/entities';
import { StationManagementService } from '@tsin-core/services/http/station-management.service';
import {
  CreateStation,
  CreateStationAssignment,
  DeleteStation,
  GetStations,
  UpdateStation,
} from './station-management.action';
import { tap, catchError } from 'rxjs';
import { IStation } from '@tsin-core/models/station-management.model';

export interface StationManagementStateModel {
  stations: IStation[];
  loading: LoadingState;
  // error: any | null;
}

@State<StationManagementStateModel>({
  name: 'stationManagementState',
  defaults: {
    stations: [],
    loading: LoadingState.notLoading,
  },
})
@Injectable()
export class StationManagementState {
  stationManagementService: StationManagementService = inject(
    StationManagementService
  );

  @Selector()
  static listStations(state: StationManagementStateModel): IStation[] {
    return state?.stations ?? [];
  }

  @Selector()
  static getLoading(state: StationManagementStateModel): LoadingState {
    return state.loading;
  }

  @Action(CreateStation)
  createStation(
    ctx: StateContext<StationManagementStateModel>,
    action: CreateStation
  ) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.stationManagementService.createStation(action.payload).pipe(
      tap((newStation: IStation) => {
        const state = ctx.getState();
        ctx.patchState({
          stations: [...state.stations, newStation],
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(CreateStationAssignment)
  createStationAssignment(
    ctx: StateContext<StationManagementStateModel>,
    action: CreateStationAssignment
  ) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.stationManagementService
      .createStationAssignment(action.payload)
      .pipe(
        tap((stationAssignment: IStation) => {
          const state = ctx.getState();
          ctx.patchState({
            stations: [...state.stations, stationAssignment],
            loading: LoadingState.notLoading,
          });
        }),
        catchError((error) => {
          ctx.patchState({ loading: LoadingState.notLoading });
          throw error;
        })
      );
  }

  @Action(GetStations)
  getStations(
    ctx: StateContext<StationManagementStateModel>,
    action: GetStations
  ) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.stationManagementService.getStations().pipe(
      tap((stations: IStation[]) => {
        ctx.patchState({ stations, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(UpdateStation)
  updateStation(
    ctx: StateContext<StationManagementStateModel>,
    action: UpdateStation
  ) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.stationManagementService.updateStation(action.editPayload).pipe(
      tap((updatedStation: IStation) => {
        const state = ctx.getState();

        const updatedStations = state.stations.map((station) =>
          station.id === updatedStation.id ? updatedStation : station
        );

        ctx.patchState({
          stations: updatedStations,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(DeleteStation)
  deleteStation(
    ctx: StateContext<StationManagementStateModel>,
    action: DeleteStation
  ) {
    ctx.patchState({ loading: LoadingState.loadingDelete });
    return this.stationManagementService.deleteStation(action.id).pipe(
      tap(() => {
        const state = ctx.getState();
        const updatedStations = state.stations.filter(
          (station) => station.id !== action.id
        );
        ctx.patchState({
          stations: updatedStations,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }
}
